import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { TemplateCategory } from '../template-categories.service';

interface TreeNode extends TemplateCategory {
  expandable: boolean;
  level: number;
  expanded?: boolean;
}

@Component({
  selector: 'app-template-category-tree',
  templateUrl: './template-category-tree.component.html',
  styleUrls: ['./template-category-tree.component.scss']
})
export class TemplateCategoryTreeComponent implements OnInit, OnChanges {
  @Input() treeData: TemplateCategory[] = [];
  @Input() allowSelection: boolean = true;
  @Input() allowMultiSelection: boolean = false;
  @Input() showTemplateCount: boolean = true;
  @Input() showActions: boolean = false;
  @Input() selectedCategoryIds: number[] = [];
  @Input() expandAllNodes: boolean = false;
  @Input() searchTerm: string = '';
  
  @Output() categorySelected = new EventEmitter<TemplateCategory>();
  @Output() categoryMultiSelected = new EventEmitter<TemplateCategory[]>();
  @Output() categoryAction = new EventEmitter<{action: string, category: TemplateCategory}>();

  treeControl = new NestedTreeControl<TreeNode>(node => node.children as TreeNode[]);
  dataSource = new MatTreeNestedDataSource<TreeNode>();
  
  filteredData: TreeNode[] = [];
  selectedCategories: TemplateCategory[] = [];

  ngOnInit(): void {
    this.processTreeData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['treeData'] || changes['searchTerm'] || changes['expandAllNodes']) {
      this.processTreeData();
    }
    
    if (changes['selectedCategoryIds']) {
      this.updateSelectedCategories();
    }
  }

  processTreeData(): void {
    this.filteredData = this.transformAndFilterData(this.treeData);
    this.dataSource.data = this.filteredData;
    
    if (this.expandAllNodes) {
      this.treeControl.expandAll();
    }
    
    this.updateSelectedCategories();
  }

  transformAndFilterData(data: TemplateCategory[], level: number = 0): TreeNode[] {
    if (!data || data.length === 0) return [];
    
    return data
      .filter(item => this.matchesSearchFilter(item))
      .map(item => {
        const node: TreeNode = {
          ...item,
          level,
          expandable: !!(item.children && item.children.length > 0)
        };
        
        if (item.children) {
          const filteredChildren = this.transformAndFilterData(item.children, level + 1);
          if (filteredChildren.length > 0) {
            node.children = filteredChildren;
            node.expandable = true;
          } else {
            node.children = [];
            node.expandable = false;
          }
        }
        
        return node;
      });
  }

  matchesSearchFilter(category: TemplateCategory): boolean {
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      return true;
    }
    
    const searchLower = this.searchTerm.toLowerCase().trim();
    
    // Check if category name matches
    if (category.name.toLowerCase().includes(searchLower)) {
      return true;
    }
    
    // Check if description matches
    if (category.description && category.description.toLowerCase().includes(searchLower)) {
      return true;
    }
    
    // Check if any child matches (recursive)
    if (category.children && category.children.length > 0) {
      return category.children.some(child => this.matchesSearchFilter(child));
    }
    
    return false;
  }

  hasChild = (_: number, node: TreeNode) => node.expandable;

  onNodeClick(node: TreeNode): void {
    if (!this.allowSelection) return;
    
    if (this.allowMultiSelection) {
      this.toggleMultiSelection(node);
    } else {
      this.selectSingleCategory(node);
    }
  }

  selectSingleCategory(category: TemplateCategory): void {
    this.selectedCategories = [category];
    this.categorySelected.emit(category);
  }

  toggleMultiSelection(category: TemplateCategory): void {
    const index = this.selectedCategories.findIndex(c => c.id === category.id);
    
    if (index > -1) {
      this.selectedCategories.splice(index, 1);
    } else {
      this.selectedCategories.push(category);
    }
    
    this.categoryMultiSelected.emit([...this.selectedCategories]);
  }

  updateSelectedCategories(): void {
    if (!this.selectedCategoryIds || this.selectedCategoryIds.length === 0) {
      this.selectedCategories = [];
      return;
    }
    
    this.selectedCategories = this.findCategoriesByIds(this.treeData, this.selectedCategoryIds);
  }

  findCategoriesByIds(data: TemplateCategory[], ids: number[]): TemplateCategory[] {
    const found: TemplateCategory[] = [];
    
    const searchRecursive = (categories: TemplateCategory[]) => {
      categories.forEach(category => {
        if (ids.includes(category.id)) {
          found.push(category);
        }
        if (category.children) {
          searchRecursive(category.children);
        }
      });
    };
    
    searchRecursive(data);
    return found;
  }

  isCategorySelected(category: TemplateCategory): boolean {
    return this.selectedCategoryIds.includes(category.id) || 
           this.selectedCategories.some(c => c.id === category.id);
  }

  onNodeAction(action: string, category: TemplateCategory): void {
    this.categoryAction.emit({ action, category });
  }

  expandAll(): void {
    this.treeControl.expandAll();
  }

  collapseAll(): void {
    this.treeControl.collapseAll();
  }

  getNodePadding(level: number): string {
    return `${level * 20}px`;
  }

  getCategoryIcon(category: TemplateCategory): string {
    if (category.children && category.children.length > 0) {
      return this.treeControl.isExpanded(category as TreeNode) ? 'folder_open' : 'folder';
    }
    return 'label';
  }

  getCategoryStatusClass(category: TemplateCategory): string {
    const classes = ['category-node'];
    
    if (!category.active) {
      classes.push('inactive');
    }
    
    if (!category.public_visible) {
      classes.push('private');
    }
    
    if (this.isCategorySelected(category)) {
      classes.push('selected');
    }
    
    return classes.join(' ');
  }

  getTemplateCounts(category: TemplateCategory): {total: number, document: number, request: number} {
    const counts = category.template_counts || { document_templates: 0, request_templates: 0, total_assignments: 0 };
    
    return {
      total: counts.total_assignments || (counts.document_templates + counts.request_templates),
      document: counts.document_templates || 0,
      request: counts.request_templates || 0
    };
  }

  // Helper method to get category path
  getCategoryPath(category: TemplateCategory): string {
    const path: string[] = [];
    
    const buildPath = (cat: TemplateCategory, allCategories: TemplateCategory[]) => {
      path.unshift(cat.name);
      
      if (cat.parent_category_id) {
        const parent = this.findCategoryById(allCategories, cat.parent_category_id);
        if (parent) {
          buildPath(parent, allCategories);
        }
      }
    };
    
    buildPath(category, this.treeData);
    return path.join(' / ');
  }

  private findCategoryById(data: TemplateCategory[], id: number): TemplateCategory | null {
    for (const category of data) {
      if (category.id === id) {
        return category;
      }
      
      if (category.children) {
        const found = this.findCategoryById(category.children, id);
        if (found) {
          return found;
        }
      }
    }
    
    return null;
  }

  // Utility methods for template
  trackByFn(index: number, item: TreeNode): number {
    return item.id;
  }

  onToggleExpand(node: TreeNode): void {
    this.treeControl.toggle(node);
  }
}